import { useEffect, useState } from 'react';
import { LayoutChangeEvent, StyleProp, TextStyle, View } from 'react-native';
import { Text } from '@/components/Text';
import { styles } from './styles';

interface ClampableTextProps {
  children: string;
  numberOfLines: number;
  expanded: boolean;
  style?: StyleProp<TextStyle>;
  onTruncatedChange?: (isTruncated: boolean) => void;
}

/**
 * onTextLayout is native-only in react-native-web, so truncation is detected by
 * comparing the full text height against the clamped height via onLayout, which
 * behaves identically on web, iOS and Android.
 */
export const ClampableText = ({
  children,
  numberOfLines,
  expanded,
  style,
  onTruncatedChange,
}: ClampableTextProps) => {
  const [fullHeight, setFullHeight] = useState<number | null>(null);
  const [clampedHeight, setClampedHeight] = useState<number | null>(null);

  useEffect(() => {
    if (fullHeight !== null && clampedHeight !== null) {
      onTruncatedChange?.(fullHeight > clampedHeight + 1);
    }
    // onTruncatedChange omitted from deps to avoid loops on inline callbacks
  }, [fullHeight, clampedHeight]);

  const measure =
    (setHeight: (height: number) => void) => (event: LayoutChangeEvent) =>
      setHeight(event.nativeEvent.layout.height);

  return (
    <View>
      <Text style={style} numberOfLines={expanded ? undefined : numberOfLines}>
        {children}
      </Text>

      <Text
        aria-hidden
        pointerEvents="none"
        style={[style, styles.measurer]}
        onLayout={measure(setFullHeight)}
      >
        {children}
      </Text>
      <Text
        aria-hidden
        pointerEvents="none"
        numberOfLines={numberOfLines}
        style={[style, styles.measurer]}
        onLayout={measure(setClampedHeight)}
      >
        {children}
      </Text>
    </View>
  );
};
